import type { FC, ReactNode } from "react";
import { ConnectProto } from "@racoon/api";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { cn } from "@racoon/ui";
import { fromTimestamp } from "@racoon/lib";
import { IcClipboardList, IcDoubleCheck, IcFileText } from "@racoon/icons";
import { Clock3 } from "lucide-react";
import { format } from "date-fns";
import { jobContainerLabel } from "../model/orders.helpers";
import { isOutgoing, whatsappMediaType, whatsappMessageKind } from "../model/whatsapp-message";

// The chat is drawn in WhatsApp's own palette: its greens, tick blue and tail notch have no token.

interface BubbleProps {
  /** The bot's side: right, green, with read ticks. The client's is left and white. */
  outgoing: boolean;
  /** A dispatcher's delivery; unset on the bot's messages, which read as delivered. */
  status?: ConnectProto.WhatsappMessageStatus;
  /** A dispatcher's message still on its way to the backend: a clock instead of ticks. */
  pending?: boolean;
  sentAt: Date | null;
  /** A form or card inside the bubble, not a line of text: more room around its content. */
  rich?: boolean;
  /** A row under the time that runs edge to edge (the template's button). */
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
}

const Bubble: FC<BubbleProps> = ({
  outgoing,
  status,
  pending,
  sentAt,
  rich,
  footer,
  className,
  children,
}) => (
  <div className={cn("flex", outgoing ? "justify-end" : "justify-start")}>
    <div
      className={cn(
        "relative rounded-lg px-2 before:absolute before:top-0 before:border-t-8 before:content-['']",
        rich ? "pt-2" : "pt-1.5",
        outgoing
          ? "rounded-tr-none bg-[#B9F8CF] text-[#00A63E] before:-right-2 before:border-r-8 before:border-r-transparent before:border-t-[#B9F8CF]"
          : "rounded-tl-none bg-white text-text-body before:-left-2 before:border-l-8 before:border-l-transparent before:border-t-white",
        className,
      )}
    >
      {children}
      {sentAt && (
        <div
          className={cn(
            "flex items-center justify-end gap-1 pb-1 text-[11px] font-medium leading-4",
            rich && "mt-1.5",
            outgoing ? "text-[#00A63E]" : "text-text-subtle",
          )}
          title={format(sentAt, "dd.MM.yyyy HH:mm")}
        >
          {format(sentAt, "HH:mm")}
          {outgoing &&
            (pending ? (
              <Clock3 className="size-3 shrink-0" data-testid="whatsapp-pending" />
            ) : (
              <DeliveryTicks status={status} />
            ))}
        </div>
      )}
      {footer}
    </div>
  </div>
);

const DeliveryTicks: FC<{ status?: ConnectProto.WhatsappMessageStatus }> = ({ status }) => {
  const t = useT();
  if (status === ConnectProto.WhatsappMessageStatus.FAILED) {
    return (
      <span className="text-text-error">{t(Namespace.Orders, "whatsappDialogue.failed")}</span>
    );
  }
  const read = !status || status === ConnectProto.WhatsappMessageStatus.READ;
  return (
    <IcDoubleCheck
      className={cn("h-2.5 w-4 shrink-0", read ? "text-[#155DFC]" : "text-text-subtle")}
      data-testid="whatsapp-read-ticks"
    />
  );
};

interface MessageBubbleProps {
  msg: ConnectProto.WhatsappMessage;
  /** The order the dialogue produced: the summary card of the client's form is read from it. */
  job: ConnectProto.Job;
}

/** One dialogue message, drawn as the bot's Flow template, the client's form, or plain text. */
export const WhatsappMessageBubble: FC<MessageBubbleProps> = ({ msg, job }) => {
  const t = useT();
  const outgoing = isOutgoing(msg);
  const sentAt = fromTimestamp(msg.createdAt) ?? null;

  switch (whatsappMessageKind(msg)) {
    case "flowTemplate":
      // The template's wording lives in WhatsApp Manager, the history stores only its name
      return (
        <Bubble
          outgoing
          sentAt={sentAt}
          className="w-[225px] max-w-full"
          footer={
            <div className="-mx-2 flex h-[41px] items-center justify-center gap-2 border-t border-border-primary text-sm font-medium">
              <IcClipboardList className="h-5 w-[18px] shrink-0" />
              {t(Namespace.Orders, "whatsappDialogue.flowTemplate.button")}
            </div>
          }
        >
          <div className="whitespace-pre-line text-sm leading-5">
            {t(Namespace.Orders, "whatsappDialogue.flowTemplate.text")}
          </div>
        </Bubble>
      );
    case "media":
      return (
        <Bubble outgoing={false} sentAt={sentAt} className="max-w-[289px]">
          <div className="text-sm italic leading-5 text-text-subtle">
            {t(
              Namespace.Orders,
              `whatsappDialogue.media.${whatsappMediaType(msg.body) ?? "other"}`,
            )}
          </div>
        </Bubble>
      );
    case "flowReply":
      return <FlowReplyBubbles job={job} sentAt={sentAt} />;
    default:
      return (
        <Bubble
          outgoing={outgoing}
          status={msg.status}
          pending={!msg.id}
          sentAt={sentAt}
          className="max-w-[289px]"
        >
          {msg.author && (
            <div
              className="text-xs font-medium leading-4 text-text-heading"
              data-testid="whatsapp-author"
            >
              {[msg.author.firstName, msg.author.lastName].filter(Boolean).join(" ")}
            </div>
          )}
          <div className="whitespace-pre-wrap break-words text-sm leading-5">{msg.body}</div>
        </Bubble>
      );
  }
};

/** What WhatsApp shows once the client submits the order Flow: «Заказ отправлен» and the form. */
const FlowReplyBubbles: FC<{ job: ConnectProto.Job; sentAt: Date | null }> = ({ job, sentAt }) => {
  const t = useT();
  const rows = [
    {
      label: t(Namespace.Orders, "whatsappDialogue.summary.site"),
      hint: t(Namespace.Orders, "whatsappDialogue.summary.siteHint"),
      value: job.pickupLocation?.name,
    },
    {
      label: t(Namespace.Orders, "whatsappDialogue.summary.container"),
      hint: t(Namespace.Orders, "whatsappDialogue.summary.containerHint"),
      value: jobContainerLabel(job),
    },
    {
      label: t(Namespace.Orders, "whatsappDialogue.summary.jobType"),
      hint: t(Namespace.Orders, "whatsappDialogue.summary.jobTypeHint"),
      value: job.type
        ? t(Namespace.Enum, `JOB_TYPE_${ConnectProto.JobType[job.type]}` as TranslationKey)
        : undefined,
    },
  ].filter((row) => row.value);

  return (
    <>
      <Bubble outgoing={false} sentAt={sentAt} rich className="w-[236px] max-w-full">
        <div className="flex h-8 items-center gap-2 rounded-lg bg-slate-100 px-3 text-xs leading-4 text-text-subtle">
          <IcFileText className="h-5 w-4 shrink-0" />
          {t(Namespace.Orders, "whatsappDialogue.flowReply.sent")}
        </div>
      </Bubble>
      {rows.length > 0 && (
        <Bubble outgoing={false} sentAt={sentAt} rich className="w-[317px] max-w-full">
          <div className="divide-y-2 divide-[#E9EEF4] rounded-lg bg-slate-100 px-2 py-[5px]">
            {rows.map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-4 py-[5px]">
                <div className="min-w-0">
                  <div className="text-sm font-medium leading-5">{row.label}</div>
                  <div className="text-xs leading-4 text-text-subtle">{row.hint}</div>
                </div>
                <div className="text-right text-sm leading-5">{row.value}</div>
              </div>
            ))}
          </div>
        </Bubble>
      )}
    </>
  );
};
